/**
 * CSV Serialization and Parsing Utility with RFC-4180 Quote Escaping
 * Ensures 100% reliable round-trip exports and imports for WEALTH.
 */

// Escape special characters and wrap in quotes if necessary
const escapeCSV = (value) => {
  if (value === null || value === undefined) return '""';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// Robust CSV line parser handling quotes and commas
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let insideQuote = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (insideQuote && line[i + 1] === '"') {
        current += '"';
        i++; // skip escaped quote
      } else {
        insideQuote = !insideQuote;
      }
    } else if (char === ',' && !insideQuote) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
};

/**
 * Converts financial application state to formatted, categorized CSV string
 */
export const stateToCSV = (state) => {
  const rows = [];

  // Header Row
  rows.push(['SECTION', 'CATEGORY', 'NAME', 'VALUE', 'FREQUENCY', 'ACCOUNT_ROUTE']);

  // 1. Core Financial Goals & Modeling
  rows.push(['GOAL', 'Core', 'Target Net Worth', state.goals.targetAmount, '', '']);
  rows.push(['GOAL', 'Core', 'Target Age', state.goals.targetAge, '', '']);
  rows.push(['GOAL', 'Core', 'Current Age', state.goals.currentAge, '', '']);
  rows.push(['GOAL', 'Core', 'Expected ROI %', state.goals.expectedROI, '', '']);
  rows.push(['GOAL', 'Core', 'Starting Net Worth', state.goals.startingNetWorth, '', '']);
  rows.push(['GOAL', 'Core', 'Inflation Rate %', state.goals.inflationRate, '', '']);
  rows.push(['GOAL', 'Modeling', 'Downturn Year', state.goals.downturnYear || 0, '', '']);
  rows.push(['GOAL', 'Modeling', 'Downturn Severity %', state.goals.downturnSeverity || 0, '', '']);
  rows.push(['GOAL', 'Modeling', 'Downturn Duration', state.goals.downturnDuration || 1, '', '']);
  rows.push(['GOAL', 'Modeling', 'Model Type', state.investmentSchedule?.type || 'fixed', '', '']);
  rows.push(['GOAL', 'Modeling', 'Linear Step Amount', state.investmentSchedule?.stepAmount || 0, '', '']);
  rows.push(['GOAL', 'Modeling', 'Linear Step Freq', state.investmentSchedule?.stepFrequency || 'monthly', '', '']);
  rows.push(['']);

  // 2. Settings & UI Preferences
  rows.push(['SETTINGS', 'UI', 'Display Frequency', state.settings?.displayFrequency || 'default', '', '']);
  rows.push(['SETTINGS', 'UI', 'Flow Filters', (state.settings?.flowFilters || []).join('|'), '', '']);
  rows.push(['']);

  // 3. Custom Accounts
  if (state.customAccounts && state.customAccounts.length > 0) {
    state.customAccounts.forEach((item) => {
      rows.push(['CUSTOM_ACCOUNT', item.category || 'drain', item.name || '', item.id || '', item.tag || '', item.purpose || '']);
    });
    rows.push(['']);
  }

  // 4. Roadmap Progress
  rows.push(['ROADMAP', 'Progress', 'CompletedSteps', (state.roadmapProgress || []).join('|'), '', '']);
  rows.push(['']);

  // 5. Income Streams
  if (state.incomeStreams && state.incomeStreams.length > 0) {
    state.incomeStreams.forEach((item) => {
      rows.push(['INCOME', 'Revenue', item.name, item.amount, item.frequency || 'monthly', item.accountType || 'income_hub']);
    });
    rows.push(['']);
  }

  // 6. Expenses
  if (state.expenses && state.expenses.length > 0) {
    state.expenses.forEach((item) => {
      rows.push(['EXPENSE', 'Drain', item.name, item.amount, item.frequency || 'monthly', item.accountType || 'bills']);
    });
    rows.push(['']);
  }

  // 7. Investment Buckets
  if (state.investmentBuckets && state.investmentBuckets.length > 0) {
    state.investmentBuckets.forEach((item) => {
      rows.push(['INVESTMENT', 'Bucket', item.name, item.amount, item.frequency || 'monthly', item.accountType || 'investing_transfer']);
    });
  }

  return rows.map((row) => row.map(escapeCSV).join(',')).join('\n');
};

/**
 * Parses CSV text into state object
 */
export const csvToState = (csvText) => {
  if (!csvText || typeof csvText !== 'string') return null;

  const lines = csvText.split(/\r?\n/);
  const newState = {
    goals: {},
    settings: { displayFrequency: 'default', flowFilters: [] },
    customAccounts: [],
    roadmapProgress: [],
    incomeStreams: [],
    expenses: [],
    investmentBuckets: [],
    investmentSchedule: { type: 'fixed', stepAmount: 0, stepFrequency: 'monthly' }
  };

  let hasImportedData = false;

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const parts = parseCSVLine(trimmed);
    if (parts.length < 3 || parts[0].toUpperCase() === 'SECTION') return;

    const [sectionRaw, categoryRaw, name, value, frequency, accountRoute] = parts;
    const section = (sectionRaw || '').toUpperCase();
    const numValue = parseFloat(value);

    hasImportedData = true;

    if (section === 'GOAL') {
      if (name === 'Target Net Worth' && !isNaN(numValue)) newState.goals.targetAmount = numValue;
      if (name === 'Target Age' && !isNaN(numValue)) newState.goals.targetAge = numValue;
      if (name === 'Current Age' && !isNaN(numValue)) newState.goals.currentAge = numValue;
      if (name === 'Expected ROI %' && !isNaN(numValue)) newState.goals.expectedROI = numValue;
      if (name === 'Starting Net Worth' && !isNaN(numValue)) newState.goals.startingNetWorth = numValue;
      if (name === 'Inflation Rate %' && !isNaN(numValue)) newState.goals.inflationRate = numValue;
      if (name === 'Downturn Year' && !isNaN(numValue)) newState.goals.downturnYear = numValue;
      if (name === 'Downturn Severity %' && !isNaN(numValue)) newState.goals.downturnSeverity = numValue;
      if (name === 'Downturn Duration' && !isNaN(numValue)) newState.goals.downturnDuration = numValue;
      if (name === 'Model Type') newState.investmentSchedule.type = value || 'fixed';
      if (name === 'Linear Step Amount' && !isNaN(numValue)) newState.investmentSchedule.stepAmount = numValue;
      if (name === 'Linear Step Freq') newState.investmentSchedule.stepFrequency = value || 'monthly';
    } else if (section === 'SETTINGS') {
      if (name === 'Display Frequency') newState.settings.displayFrequency = value || 'default';
      if (name === 'Flow Filters') newState.settings.flowFilters = value ? value.split('|').filter(Boolean) : [];
    } else if (section === 'CUSTOM_ACCOUNT') {
      if (name && value) {
        newState.customAccounts.push({
          id: value,
          name: name,
          category: categoryRaw || 'drain',
          tag: frequency || 'Custom',
          purpose: accountRoute || 'User defined custom route.'
        });
      }
    } else if (section === 'ROADMAP') {
      if (name === 'CompletedSteps') {
        newState.roadmapProgress = value ? value.split('|').filter(Boolean) : [];
      }
    } else if (section === 'INCOME') {
      if (name && !isNaN(numValue)) {
        newState.incomeStreams.push({
          id: Date.now() + Math.random(),
          name,
          amount: numValue,
          frequency: frequency || 'monthly',
          accountType: accountRoute || 'income_hub'
        });
      }
    } else if (section === 'EXPENSE') {
      if (name && !isNaN(numValue)) {
        newState.expenses.push({
          id: Date.now() + Math.random(),
          name,
          amount: numValue,
          frequency: frequency || 'monthly',
          accountType: accountRoute || 'bills'
        });
      }
    } else if (section === 'INVESTMENT') {
      if (name && !isNaN(numValue)) {
        newState.investmentBuckets.push({
          id: Date.now() + Math.random(),
          name,
          amount: numValue,
          frequency: frequency || 'monthly',
          accountType: accountRoute || 'investing_transfer',
          icon: 'TrendingUp',
          color: 'blue'
        });
      }
    }
  });

  return hasImportedData ? newState : null;
};